import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { KeyRound, Plus, Pencil, Trash2, Eye, EyeOff, Lock, Cpu } from 'lucide-react';
import companyAdminService from '../../services/companyAdminService';
import apiService from '../../services/apiService';
import { usePermissions } from '../../context/PermissionsContext';
import { KEYS_COPY } from '../../constants/partnerCopy';
import { describeApiError } from '../../utils/apiError';
import {
  providerHelperText,
  resolveInheritedForCompanyAdmin,
  isBlockedTone,
} from '../shared/LLMKeys/llmKeyStatus';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

// Mirrors the UI handoff's getEffectiveStatus() exactly — resolution order is
// personal key (not this screen) → company key → platform fallback → none.
const getEffectiveStatus = (credential) => {
  if (!credential?.is_active) return 'none';
  if (credential.has_key) return 'company';
  if (credential.allow_platform_fallback) return 'platform';
  return 'none';
};

const STATUS_META = {
  company: { label: 'Company key', tone: 'success' },
  platform: { label: 'Platform key', tone: 'info' },
  none: { label: 'Not configured', tone: 'blocked' },
};

// Cohere is an optional KB search re-ranking provider, not a chat model, so
// "no key" isn't an error — its badges read softer/neutral. Kept identical to
// the Platform Admin console + My LLM Keys screens for consistency.
const COHERE_STATUS_META = {
  company: { label: 'Re-ranking: company key', tone: 'success' },
  platform: { label: 'Re-ranking: platform', tone: 'info' },
  none: { label: 'Re-ranking off', tone: 'neutral' },
};

/**
 * Company Admin "LLM Providers" tab (UI implementation handoff doc).
 * Lets a company admin set/replace/remove the company-wide key for each LLM
 * provider. Scoped entirely to the caller's own company by the backend — no
 * company_id is ever sent. The platform-fallback flag is read-only here; only
 * platform admins may toggle it (see PlatformAdmin/CompaniesPanel.jsx).
 *
 * A partner's client company (Partner tier guide §5.5): without its own key a
 * provider comes from the partner — "From <Partner>" — unless the partner turned
 * inheritance off for it — "Turned off by <Partner>". The company admin can see
 * but not change inheritance, and can't see whether the partner has a key.
 */
const CompanyLLMProvidersPanel = ({ notify }) => {
  const { partner, isPartnerStaff } = usePermissions();
  const partnerManaged = !!partner && !isPartnerStaff;
  const partnerName = partner?.name;
  const [providers, setProviders] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [keyDialog, setKeyDialog] = useState(null); // selected row
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [baseUrlValue, setBaseUrlValue] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [providerData, credentialData] = await Promise.all([
        apiService.getProviders(true, 200),
        companyAdminService.getLLMKeys(),
      ]);
      setProviders(Array.isArray(providerData) ? providerData : providerData?.providers || []);
      setCredentials(
        Array.isArray(credentialData) ? credentialData : credentialData?.credentials || []
      );
    } catch (err) {
      setError(err?.message || 'Failed to load company LLM providers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Join providers x credentials into one row per active provider.
  const rows = useMemo(() => {
    const credByProvider = new Map(credentials.map((c) => [c.provider_id, c]));
    return providers.map((provider) => {
      const cred = credByProvider.get(provider.id);
      const slug = String(provider.name || '').toLowerCase();
      const isCohere = slug === 'cohere';
      const status = getEffectiveStatus(cred);
      let badge = (isCohere ? COHERE_STATUS_META : STATUS_META)[status];
      if (partnerManaged && status !== 'company') {
        const inherited = resolveInheritedForCompanyAdmin(cred, partnerName);
        badge =
          isCohere && isBlockedTone(inherited.tone)
            ? { ...inherited, label: 'Re-ranking off', tone: 'neutral' }
            : inherited;
      }
      return {
        providerId: provider.id,
        name: provider.display_name || provider.name,
        baseUrl: cred?.base_url || provider.base_url || null,
        isOllama: slug === 'ollama',
        isCohere,
        helperText: providerHelperText(slug),
        // OpenAI powers KB embeddings; blocked → this company can't upload files
        // or run KB-enabled agents.
        openaiBlocked: slug === 'openai' && isBlockedTone(badge.tone),
        hasKey: !!cred?.has_key,
        keyLast4: cred?.key_last4 || null,
        status,
        badge,
      };
    });
  }, [providers, credentials, partnerManaged, partnerName]);

  const openEditor = (row) => {
    setKeyDialog(row);
    setApiKeyValue('');
    setBaseUrlValue(row.baseUrl || '');
    setShowKey(false);
  };

  const closeEditor = () => {
    if (saving) return;
    setKeyDialog(null);
    setApiKeyValue('');
    setBaseUrlValue('');
    setShowKey(false);
  };

  // Replace the provider's credential state with the returned response,
  // rather than a full refetch — both PUT and DELETE return the updated row.
  const upsertCredential = (updated) => {
    if (!updated || updated.provider_id == null) return;
    setCredentials((prev) => {
      const idx = prev.findIndex((c) => c.provider_id === updated.provider_id);
      if (idx === -1) return [...prev, updated];
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
  };

  const handleSave = async () => {
    if (!keyDialog || !apiKeyValue.trim()) return;
    try {
      setSaving(true);
      const payload = { api_key: apiKeyValue.trim() };
      if (baseUrlValue.trim()) payload.base_url = baseUrlValue.trim();
      const updated = await companyAdminService.setLLMKey(keyDialog.providerId, payload);
      upsertCredential(updated);
      // Server caches keys — changes can take up to 5 minutes (§5.5).
      notify(KEYS_COPY.saved, 'success');
      closeEditor();
    } catch (err) {
      notify(describeApiError(err, 'Failed to save company key.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      const updated = await companyAdminService.deleteLLMKey(removeTarget.providerId);
      upsertCredential(updated);
      notify(`${removeTarget.name} company key removed. ${KEYS_COPY.saved.replace(/^Saved\. /, '')}`, 'info');
      setRemoveTarget(null);
    } catch (err) {
      notify(describeApiError(err, 'Failed to remove company key.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Box className={styles.noteBanner}>
        <KeyRound size={18} style={{ flexShrink: 0, marginTop: 1 }} />
        {partnerManaged ? (
          <span>
            Company keys are used by any teammate who hasn't set a personal key for that provider.
            Personal keys always take priority. Without a company key, {partnerName} (your partner)
            provides the provider — unless it has turned that provider off for your company.
          </span>
        ) : (
          <span>
            Company keys are used by any teammate who hasn't set a personal key for that provider.
            Personal keys always take priority; the platform key is used only if your platform admin
            has enabled fallback for a provider.
          </span>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : rows.length === 0 ? (
        <Box className={styles.emptyState}>
          <KeyRound className={styles.emptyIcon} size={56} />
          <Typography variant="h6">No providers available</Typography>
          <Typography variant="body2">There are no active LLM providers to configure yet.</Typography>
        </Box>
      ) : (
        <Box className={styles.tableCard}>
          {rows.map((row) => {
            const meta = row.badge;
            const badge = <span className={`${styles.effBadge} ${styles[meta.tone]}`}>{meta.label}</span>;
            return (
              <Box key={row.providerId} className={styles.userRow} sx={{ flexWrap: 'wrap' }}>
                <Box className={styles.companyMain} sx={{ flex: '1 1 260px' }}>
                  <span className={styles.companyAvatar}>
                    <Cpu size={18} />
                  </span>
                  <Box>
                    <span className={styles.companyName}>{row.name}</span>
                    {row.openaiBlocked ? (
                      <span className={styles.providerWarn}>
                        {partnerManaged
                          ? `Off right now — ${partnerName} has turned OpenAI off for your company, so your team can't upload files or run knowledge-base agents. Add an OpenAI company key, or ask your partner.`
                          : "Off right now — with no company key and platform fallback off, your team can't upload files or run knowledge-base agents. Add an OpenAI company key, or ask your platform admin to enable fallback."}
                      </span>
                    ) : (
                      row.helperText && <span className={styles.providerHint}>{row.helperText}</span>
                    )}
                    {row.baseUrl && <span className={styles.companyMeta}>{row.baseUrl}</span>}
                  </Box>
                </Box>

                {!row.isOllama &&
                  (meta.tooltip ? (
                    <Tooltip title={meta.tooltip} arrow>
                      {badge}
                    </Tooltip>
                  ) : (
                    badge
                  ))}

                <span className={styles.keyCell}>
                  {row.hasKey ? (
                    <span className={styles.keyMask}>&#8226;&#8226;&#8226;&#8226;{row.keyLast4}</span>
                  ) : (
                    <span className={styles.keyEmpty}>&mdash;</span>
                  )}
                </span>

                <Box className={styles.actionsCell}>
                  {row.isOllama ? (
                    <Tooltip title="Ollama runs locally and doesn't need an API key.">
                      <span>
                        <Button size="small" variant="outlined" className={styles.outlineBtn} disabled>
                          No API key required
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      <Button
                        size="small"
                        variant={row.hasKey ? 'outlined' : 'contained'}
                        className={row.hasKey ? styles.outlineBtn : styles.primaryBtn}
                        startIcon={row.hasKey ? <Pencil size={14} /> : <Plus size={14} />}
                        onClick={() => openEditor(row)}
                      >
                        {row.hasKey ? 'Update' : 'Set key'}
                      </Button>
                      {row.hasKey && (
                        <IconButton
                          size="small"
                          className={styles.dangerIcon}
                          title="Remove company key"
                          onClick={() => setRemoveTarget(row)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Set / Update key dialog */}
      <Dialog
        open={!!keyDialog}
        onClose={closeEditor}
        classes={{ paper: styles.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={styles.dialogTitle}>
          {keyDialog?.hasKey ? 'Update' : 'Set'} {keyDialog?.name} company key
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <TextField
            autoFocus
            label="API key"
            type={showKey ? 'text' : 'password'}
            fullWidth
            required
            variant="outlined"
            placeholder="sk-..."
            value={apiKeyValue}
            onChange={(e) => setApiKeyValue(e.target.value)}
            autoComplete="new-password"
            className={styles.formField}
            helperText={
              keyDialog?.hasKey
                ? 'Entering a new key replaces the existing one company-wide.'
                : 'Stored securely. Only the last 4 characters are ever shown again.'
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    onClick={() => setShowKey((v) => !v)}
                    title={showKey ? 'Hide key' : 'Show key'}
                  >
                    {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Base URL (optional)"
            type="url"
            fullWidth
            variant="outlined"
            placeholder="https://custom-provider.example/v1"
            value={baseUrlValue}
            onChange={(e) => setBaseUrlValue(e.target.value)}
            autoComplete="off"
            className={styles.formField}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeEditor} disabled={saving} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !apiKeyValue.trim()}
            className={styles.primaryBtn}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog
        open={!!removeTarget}
        onClose={() => !removing && setRemoveTarget(null)}
        classes={{ paper: styles.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={styles.dialogTitle}>Remove company key</DialogTitle>
        <DialogContent className={styles.deleteContent}>
          <Box className={styles.warningIconWrap}>
            <Lock />
          </Box>
          <Typography className={styles.deleteHeading}>
            Remove the company key for {removeTarget?.name}?
          </Typography>
          <Typography className={styles.deleteText}>
            {partnerManaged
              ? `Users without personal keys will use ${partnerName}'s key, unless your partner has turned this provider off for your company.`
              : 'Users without personal keys will use the platform key if fallback is enabled; otherwise, they will lose access to this provider.'}
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRemove}
            disabled={removing}
            className={styles.dangerButton}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={16} />}
          >
            {removing ? 'Removing...' : 'Remove key'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyLLMProvidersPanel;
